<script lang="ts">
import {
    defineComponent,
    isRef,
    ref,
    h,
    Fragment,
    onMounted,
    Ref,
    watch,
    getCurrentInstance
} from 'vue';
import isDOMElement from '../../../utils/isDOMElement';
import useTippy from './useTippy';
import tippy from 'tippy.js';
import type { TippyOptions } from './types';

// const pluginProps = [
//   'animateFill',
//   'followCursor',
//   'inlinePositioning',
//   'sticky',
// ]

interface Props extends TippyOptions {
    trigger: 'mouseenter focus' | 'click' | 'focusin' | 'mouseenter click' | 'manual';
    referenceElement?: string | Element | Ref<Element>;
    disabled?: boolean;
}

const booleanProps = [
    'a11y',
    'allowHTML',
    'arrow',
    'flip',
    'flipOnUpdate',
    'hideOnClick',
    'ignoreAttributes',
    'inertia',
    'interactive',
    'lazy',
    'multiple',
    'showOnInit',
    'touch',
    'touchHold'
];

const props: any = {};

const plugins = ['inlinePositioning', 'followCursor', 'sticky', 'animateFill'];

Object.keys(tippy.defaultProps).forEach(_ => {
    const prop = _ as keyof TippyOptions;
    if (plugins?.includes(prop)) return;
    if (booleanProps.includes(prop)) {
        props[prop] = {
            type: Boolean,
            default: function () {
                return tippy.defaultProps[prop as keyof TippyOptions] as Boolean;
            }
        };
    } else {
        props[prop] = {
            default: function () {
                return tippy.defaultProps[prop as keyof TippyOptions];
            }
        };
    }
});

export default defineComponent({
    name: 'PtPopper',
    props: {
        ...props,
        zIndex: {
            type: Number,
            default: 1999
        },
        interactive: {
            type: Boolean,
            default: true
        },
        appendTo: {
            type: [Object] as unknown as () => Props['appendTo'],
            default: document.body
        },
        referenceElement: {
            type: [String, Object] as unknown as () => Props['referenceElement'],
            default: undefined
        },
        disabled: {
            type: Boolean
        }
    },
    emits: ['hidden', 'hide'],
    setup(props: Props, { slots, emit }) {
        const instance = getCurrentInstance();
        const onHide = () => emit('hide');
        const onHidden = () => emit('hidden');

        const referenceEle = ref<NonDocumentTypeChildNode | undefined>();
        const contentEle = ref<NonDocumentTypeChildNode | undefined>();

        let options = {
            ...props,
            onHide,
            onHidden
        } as Props;
        delete options.referenceElement;
        delete options.disabled;

        let reference: Element | Ref<NonDocumentTypeChildNode | undefined> | undefined | null =
            referenceEle;

        const tippyInstance = ref<ReturnType<typeof useTippy>>();

        onMounted(() => {
            if (props.referenceElement) {
                if (isDOMElement(props.referenceElement)) {
                    reference = props.referenceElement as unknown as Element;
                } else if (
                    typeof props.referenceElement === 'string' ||
                    props.referenceElement instanceof String
                ) {
                    reference = document.querySelector(props.referenceElement as string);
                } else if (
                    isRef(props.referenceElement) &&
                    props.referenceElement.value instanceof Element
                ) {
                    reference = props.referenceElement.value;
                }
            } else {
                if (referenceEle.value?.nextElementSibling)
                    reference = referenceEle.value.nextElementSibling;
            }

            tippyInstance.value = useTippy(reference as Element, options);
            if (slots.default && contentEle.value?.nextElementSibling)
                tippyInstance.value.setContent(() => contentEle.value?.nextElementSibling);
            if (props.disabled) tippyInstance.value.disable();
            (instance as unknown as any).proxy.tippyInstance = tippyInstance.value;
        });

        watch(
            () => props.disabled,
            (newDisabled, oldDisabled) => {
                if (!tippyInstance.value) return;
                if (newDisabled === true) tippyInstance.value.disable();
                if (oldDisabled === true && newDisabled !== true) tippyInstance.value.enable();
            },
            { immediate: true }
        );

        // return {
        //     referenceEle,
        //     contentEle,
        //     tippyInstance: tippy,
        // }

        return () =>
            h(Fragment, { ref: referenceEle }, [
                slots?.reference ? slots.reference?.() : null,
                slots?.default ? h(Fragment, { ref: contentEle }, slots?.default?.()) : null
            ]);
    }
    // render() {
    //     return h(
    //         Fragment,
    //         { ref: this.referenceEle },
    //         [
    //             this.$slots?.reference ?
    //                 this.$slots.reference?.()
    //                 : null,
    //             this.$slots?.default ?
    //                 createBlock(
    //                     Fragment,
    //                     { ref: this.contentEle },
    //                     this.$slots?.default?.()
    //                 )
    //                 : null
    //         ]
    //     )
    // }
});
</script>
